import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { toast } from 'sonner';
import { Plus, Trash2, Search, Cpu, PackageMinus, Edit, Wrench, Store, Calendar as CalIcon, Gamepad2, ChevronsUpDown, Check } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

// Fournisseurs proposés par défaut (l'utilisateur peut en saisir d'autres,
// qui enrichissent alors la liste partagée côté serveur).
const DEFAULT_SUPPLIERS = ['AliExpress', 'Amazon', 'Farnell', 'Mouser', 'DigiKey', 'RS Components', 'Reichelt', 'Conrad', 'LCSC', 'eBay', 'Le Bon Coin'];

const emptyForm = () => ({
  name: '', reference: '', category: '', supplier: '',
  price: '', transport_cost: '', quantity_purchased: 1,
  purchase_date: new Date().toISOString().split('T')[0],
  price_breakdown: [], notes: '',
});

const SpareParts = () => {
  const { t, API, canEdit } = useApp();
  const navigate = useNavigate();
  const [parts, setParts] = useState([]);
  const [users, setUsers] = useState([]);
  const [cabinets, setCabinets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]); // valeurs enrichies (liste serveur)
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const [showUse, setShowUse] = useState(false);
  const [usePart, setUsePart] = useState(null);
  const [useForm, setUseForm] = useState({ cabinet_id: '', quantity_used: 1, date: '', description: '' });
  const [cabPickerOpen, setCabPickerOpen] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [p, u, c, cat, sup] = await Promise.all([
        axios.get(`${API}/spare-parts`),
        axios.get(`${API}/users/list`).catch(() => ({ data: [] })),
        axios.get(`${API}/cabinets`),
        axios.get(`${API}/lists/component`).catch(() => ({ data: [] })),
        axios.get(`${API}/lists/supplier`).catch(() => ({ data: [] })),
      ]);
      const sortBy = (arr, key) => [...arr].sort((a, b) => (a[key] || '').localeCompare(b[key] || '', undefined, { sensitivity: 'base' }));
      setParts(p.data);
      setUsers(sortBy(u.data, 'nickname'));
      setCabinets(sortBy(c.data, 'friendly_name'));
      setCategories(sortBy(cat.data, 'value'));
      setSuppliers(sup.data);
    } catch (e) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  // Suggestions = défauts + valeurs déjà saisies (dédupliquées, insensible à la casse)
  const supplierSuggestions = (() => {
    const seen = new Set();
    const out = [];
    [...DEFAULT_SUPPLIERS, ...suppliers.map((s) => s.value)].forEach((v) => {
      const k = (v || '').trim().toLowerCase();
      if (v && !seen.has(k)) { seen.add(k); out.push(v); }
    });
    return out.sort((a, b) => a.localeCompare(b));
  })();

  // Enrichit la liste 'supplier' côté serveur si la valeur est nouvelle (best-effort)
  const enrichSupplier = async (value) => {
    const v = (value || '').trim();
    if (!v) return;
    const exists = suppliers.some((s) => (s.value || '').trim().toLowerCase() === v.toLowerCase())
      || DEFAULT_SUPPLIERS.some((s) => s.toLowerCase() === v.toLowerCase());
    if (exists) return;
    try { await axios.post(`${API}/lists`, { list_type: 'supplier', value: v }); } catch (e) { /* best-effort */ }
  };

  const fmt = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const num = (v) => (v === '' || v === null || v === undefined ? 0 : parseFloat(v) || 0);

  // ---------- create / edit ----------
  const openCreate = () => { setEditingPart(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (p) => {
    setEditingPart(p);
    setForm({
      name: p.name || '', reference: p.reference || '', category: p.category || '', supplier: p.supplier || '',
      price: p.price ?? '', transport_cost: p.transport_cost ?? '',
      quantity_purchased: p.quantity_purchased || 1,
      purchase_date: p.purchase_date || new Date().toISOString().split('T')[0],
      price_breakdown: p.price_breakdown || [], notes: p.notes || '',
    });
    setShowForm(true);
  };

  const addInvestor = () => setForm((f) => ({ ...f, price_breakdown: [...f.price_breakdown, { user_id: '', nickname: '', amount: '' }] }));
  const updateInvestor = (i, field, value) => setForm((f) => {
    const pb = [...f.price_breakdown];
    pb[i] = { ...pb[i], [field]: value };
    if (field === 'user_id') pb[i].nickname = users.find((u) => u.id === value)?.nickname || '';
    return { ...f, price_breakdown: pb };
  });
  const removeInvestor = (i) => setForm((f) => ({ ...f, price_breakdown: f.price_breakdown.filter((_, idx) => idx !== i) }));

  const formTotal = num(form.price) + num(form.transport_cost);
  const formBreakdownTotal = form.price_breakdown.reduce((s, inv) => s + num(inv.amount), 0);
  const formUnit = form.quantity_purchased > 0 ? formTotal / form.quantity_purchased : 0;

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(t('errors.requiredField')); return; }
    if (form.price_breakdown.length > 0 && Math.abs(formBreakdownTotal - formTotal) > 0.01) {
      toast.error(t('spareParts.totalMustEqual')); return;
    }
    const payload = {
      name: form.name.trim(),
      reference: form.reference || null,
      category: form.category || null,
      supplier: form.supplier?.trim() || null,
      price: num(form.price),
      transport_cost: num(form.transport_cost),
      quantity_purchased: parseInt(form.quantity_purchased) || 1,
      purchase_date: form.purchase_date || null,
      price_breakdown: form.price_breakdown.length > 0 ? form.price_breakdown : null,
      notes: form.notes || null,
    };
    try {
      if (editingPart) await axios.put(`${API}/spare-parts/${editingPart.id}`, payload);
      else await axios.post(`${API}/spare-parts`, payload);
      await enrichSupplier(form.supplier); // ajoute le fournisseur à la liste partagée si nouveau
      toast.success(t('common.success'));
      setShowForm(false); fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errors.serverError'));
    }
  };

  // ---------- use ----------
  const openUse = (p) => {
    setUsePart(p);
    setUseForm({
      cabinet_id: '', quantity_used: 1,
      date: new Date().toISOString().split('T')[0],
      description: '',
    });
    setShowUse(true);
  };

  const useAmount = usePart ? Math.round(usePart.unit_cost * (parseInt(useForm.quantity_used) || 0) * 100) / 100 : 0;
  const computeSplit = () => {
    if (!usePart?.price_breakdown?.length) return [];
    const total = usePart.price_breakdown.reduce((s, i) => s + num(i.amount), 0);
    if (total <= 0) return [];
    return usePart.price_breakdown.map((i) => ({
      nickname: i.nickname || 'Unknown',
      amount: Math.round(useAmount * (num(i.amount) / total) * 100) / 100,
    }));
  };
  const defaultUseDesc = () => (usePart
    ? t('spareParts.defaultUseDescription').replace('{name}', usePart.name).replace('{qty}', useForm.quantity_used)
    : '');

  const handleUse = async () => {
    if (!useForm.cabinet_id) { toast.error(t('errors.requiredField')); return; }
    const qty = parseInt(useForm.quantity_used) || 0;
    if (qty <= 0 || qty > usePart.quantity_available) { toast.error(t('spareParts.quantityUsed')); return; }
    try {
      await axios.post(`${API}/spare-parts/${usePart.id}/use`, {
        cabinet_id: useForm.cabinet_id,
        quantity_used: qty,
        date: useForm.date || null,
        description: useForm.description?.trim() || defaultUseDesc(),
      });
      toast.success(t('common.success'));
      setShowUse(false); fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errors.serverError'));
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/spare-parts/${toDelete.id}`);
      toast.success(t('common.success'));
      setShowDelete(false); setToDelete(null); fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errors.serverError'));
    }
  };

  const filtered = parts.filter((p) => {
    const q = search.toLowerCase();
    return p.name.toLowerCase().includes(q)
      || (p.reference || '').toLowerCase().includes(q)
      || (p.category || '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="spare-parts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow text-[11px] text-primary/80 mb-2">Arcade Fleet · Stock Manager</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.08em] uppercase sunset-text">
            {t('spareParts.title')}
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-xs">{t('spareParts.subtitle')}</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} className="font-display uppercase tracking-[0.16em] glow-amber" data-testid="add-spare-btn">
            <Plus className="w-4 h-4 mr-2" /> {t('spareParts.addPart')}
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder={t('spareParts.search')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 font-mono" />
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Card className="bezel p-12 text-center">
          <Cpu className="w-16 h-16 mx-auto text-primary/40 mb-4" />
          <p className="text-muted-foreground">{t('spareParts.noData')}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const usedQty = p.quantity_purchased - p.quantity_available;
            const pct = p.quantity_purchased > 0 ? (p.quantity_available / p.quantity_purchased) * 100 : 0;
            return (
              <Card key={p.id} className="bezel flex flex-col">
                <CardContent className="p-5 space-y-4 flex-1 flex flex-col">
                  {/* Head */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-md bg-primary/10 flex items-center justify-center shrink-0 glow-amber">
                        <Cpu className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold truncate">{p.name}</p>
                        <p className="text-xs text-muted-foreground font-mono truncate">
                          {[p.category, p.reference].filter(Boolean).join(' · ') || (p.quantity_purchased > 1 ? t('spareParts.lot') : t('spareParts.unit'))}
                        </p>
                      </div>
                    </div>
                    <Badge className={p.in_stock
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 uppercase text-[10px] font-bold shrink-0'
                      : 'bg-secondary/20 text-secondary border border-secondary/40 uppercase text-[10px] font-bold shrink-0'}>
                      {p.in_stock ? t('spareParts.inStock') : t('spareParts.outOfStock')}
                    </Badge>
                  </div>

                  {/* Stock bar */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-mono">
                        <span className="font-bold text-foreground tabular-nums">{p.quantity_available}</span>
                        <span className="text-muted-foreground"> / {p.quantity_purchased} {t('spareParts.quantityAvailable').toLowerCase()}</span>
                      </span>
                      {usedQty > 0 && <span className="text-xs text-muted-foreground font-mono">{usedQty} {t('spareParts.used')}</span>}
                    </div>
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div className={`h-full transition-all duration-500 ${p.in_stock ? 'bg-primary' : 'bg-secondary'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>

                  {/* Costs */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="rounded-md bg-muted/40 border border-border p-2">
                      <p className="eyebrow text-[9px] text-muted-foreground">{t('spareParts.unitCost')}</p>
                      <p className="font-mono font-bold tabular-nums">{fmt(p.unit_cost)}</p>
                    </div>
                    <div className="rounded-md bg-muted/40 border border-border p-2">
                      <p className="eyebrow text-[9px] text-muted-foreground">{t('spareParts.totalCost')}</p>
                      <p className="font-mono font-bold tabular-nums">{fmt(p.total_cost)}</p>
                    </div>
                  </div>

                  {/* Meta: supplier + purchase date */}
                  {(p.supplier || p.purchase_date) && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground font-mono">
                      {p.supplier && <span className="flex items-center gap-1"><Store className="w-3 h-3" /> {p.supplier}</span>}
                      {p.purchase_date && <span className="flex items-center gap-1"><CalIcon className="w-3 h-3" /> {new Date(p.purchase_date).toLocaleDateString()}</span>}
                    </div>
                  )}

                  {/* Investors */}
                  {p.price_breakdown?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {p.price_breakdown.map((inv, i) => (
                        <Badge key={i} variant="outline" className="text-[11px] font-mono border-border bg-muted/40">
                          {inv.nickname}: {fmt(num(inv.amount))}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Used on cabinets */}
                  {p.used_on?.length > 0 && (
                    <div className="space-y-1">
                      <p className="eyebrow text-[9px] text-muted-foreground flex items-center gap-1">
                        <Gamepad2 className="w-3 h-3" /> {t('spareParts.usedOn')}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {p.used_on.map((u) => (
                          <Badge
                            key={u.cabinet_id}
                            variant="outline"
                            className="text-[11px] font-mono border-border bg-muted/40 cursor-pointer hover:border-primary hover:text-primary transition-colors"
                            onClick={() => navigate(`/cabinets/${u.cabinet_id}`)}
                            title={t('spareParts.usedOn')}
                          >
                            {u.cabinet_name} ×{u.quantity}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {canEdit && (
                    <div className="flex gap-2 pt-1 mt-auto">
                      <Button size="sm" className="flex-1 font-display uppercase tracking-[0.08em]" disabled={!p.in_stock} onClick={() => openUse(p)} data-testid={`use-spare-${p.id}`}>
                        <Wrench className="w-4 h-4 mr-1" /> {t('spareParts.use')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-spare-${p.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => { setToDelete(p); setShowDelete(true); }} data-testid={`delete-spare-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ---------- CREATE / EDIT DIALOG ---------- */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bezel">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-[0.06em]">
              {editingPart ? t('spareParts.editPart') : t('spareParts.addPart')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.name')} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('spareParts.namePlaceholder')} data-testid="spare-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.reference')}</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.category')}</Label>
                <Input list="spare-cat-list" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                <datalist id="spare-cat-list">
                  {categories.map((c) => <option key={c.id} value={c.value} />)}
                </datalist>
              </div>
            </div>

            {/* Fournisseur / magasin (liste enrichissable + saisie libre) */}
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground flex items-center gap-1">
                <Store className="w-3 h-3" /> {t('spareParts.supplier')}
              </Label>
              <Input
                list="spare-supplier-list"
                value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                placeholder={t('spareParts.supplierPlaceholder')}
                data-testid="spare-supplier-input"
              />
              <datalist id="spare-supplier-list">
                {supplierSuggestions.map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.price')} (€)</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0.00" data-testid="spare-price-input" />
              </div>
              <div className="space-y-2">
                <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.transportCost')} (€)</Label>
                <Input type="number" step="0.01" value={form.transport_cost} onChange={(e) => setForm({ ...form, transport_cost: e.target.value })} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.quantityPurchased')}</Label>
                <Input type="number" min="1" value={form.quantity_purchased} onChange={(e) => setForm({ ...form, quantity_purchased: e.target.value })} data-testid="spare-qty-input" />
              </div>
            </div>

            {/* live unit cost */}
            <div className="flex items-center justify-between rounded-md bg-muted/40 border border-border px-3 py-2 text-sm">
              <span className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.unitCost')}</span>
              <span className="font-mono font-bold text-primary tabular-nums">{fmt(formUnit)}</span>
            </div>

            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.purchaseDate')}</Label>
              <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="font-mono" />
            </div>

            {/* Investors */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.investors')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addInvestor}>
                  <Plus className="w-4 h-4 mr-1" /> {t('spareParts.addInvestor')}
                </Button>
              </div>
              {form.price_breakdown.map((inv, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Select value={inv.user_id} onValueChange={(v) => updateInvestor(i, 'user_id', v)}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder={t('spareParts.investor')} /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.nickname}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" step="0.01" className="w-24" placeholder="€" value={inv.amount} onChange={(e) => updateInvestor(i, 'amount', e.target.value)} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeInvestor(i)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              ))}
              {form.price_breakdown.length > 0 && (
                <p className={`text-xs font-mono ${Math.abs(formBreakdownTotal - formTotal) > 0.01 ? 'text-destructive' : 'text-emerald-400'}`}>
                  {fmt(formBreakdownTotal)} / {fmt(formTotal)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.notes')}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} className="font-display uppercase tracking-[0.12em] glow-amber" data-testid="save-spare-btn">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- USE DIALOG ---------- */}
      <Dialog open={showUse} onOpenChange={setShowUse}>
        <DialogContent className="sm:max-w-md bezel">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-[0.06em] flex items-center gap-2">
              <PackageMinus className="w-5 h-5 text-primary" /> {t('spareParts.useTitle')}
            </DialogTitle>
          </DialogHeader>
          {usePart && (
            <div className="space-y-4 py-2">
              <div className="rounded-md bg-muted/40 border border-border px-3 py-2 text-sm flex items-center justify-between">
                <span className="font-bold truncate">{usePart.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{usePart.quantity_available} {t('spareParts.available')}</span>
              </div>

              <div className="space-y-2">
                <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.cabinet')} *</Label>
                <Popover open={cabPickerOpen} onOpenChange={setCabPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="use-cabinet-select">
                      <span className="truncate">
                        {useForm.cabinet_id
                          ? (() => { const c = cabinets.find((x) => x.id === useForm.cabinet_id); return c ? `${c.friendly_name} (${c.unique_id})` : t('common.selectOption'); })()
                          : t('common.selectOption')}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder={t('spareParts.searchCabinet')} />
                      <CommandList>
                        <CommandEmpty>{t('common.noResults')}</CommandEmpty>
                        <CommandGroup>
                          {cabinets.map((cab) => (
                            <CommandItem
                              key={cab.id}
                              value={`${cab.friendly_name} ${cab.unique_id}`}
                              onSelect={() => { setUseForm({ ...useForm, cabinet_id: cab.id }); setCabPickerOpen(false); }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${useForm.cabinet_id === cab.id ? 'opacity-100' : 'opacity-0'}`} />
                              <span className="truncate">{cab.friendly_name} <span className="text-muted-foreground font-mono text-xs">({cab.unique_id})</span></span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.quantityUsed')}</Label>
                  <Input type="number" min="1" max={usePart.quantity_available} value={useForm.quantity_used}
                    onChange={(e) => setUseForm({ ...useForm, quantity_used: e.target.value })} data-testid="use-qty-input" />
                </div>
                <div className="space-y-2">
                  <Label className="eyebrow text-[10px] text-muted-foreground">{t('operations.date')}</Label>
                  <Input type="date" value={useForm.date} onChange={(e) => setUseForm({ ...useForm, date: e.target.value })} className="font-mono" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.description')}</Label>
                <Input value={useForm.description} placeholder={defaultUseDesc()}
                  onChange={(e) => setUseForm({ ...useForm, description: e.target.value })} />
              </div>

              {/* Cost preview */}
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="eyebrow text-[10px] text-muted-foreground">{t('spareParts.costPreview')}</span>
                  <span className="font-display font-bold text-primary tabular-nums">{fmt(useAmount)}</span>
                </div>
                {computeSplit().length > 0 && (
                  <div className="pt-2 border-t border-border/60">
                    <p className="eyebrow text-[9px] text-muted-foreground mb-1">{t('spareParts.investorSplit')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {computeSplit().map((s, i) => (
                        <Badge key={i} variant="outline" className="text-[11px] font-mono border-border bg-muted/40">
                          {s.nickname}: {fmt(s.amount)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUse(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleUse} className="font-display uppercase tracking-[0.12em] glow-amber" data-testid="confirm-use-btn">
              {t('spareParts.use')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- DELETE ---------- */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('spareParts.deletePart')}</AlertDialogTitle>
            <AlertDialogDescription>{t('spareParts.confirmDelete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive" data-testid="confirm-delete-spare">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SpareParts;
